import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { 
    FaUsers, 
    FaUserPlus, 
    FaEdit, 
    FaTrash, 
    FaSearch,
    FaSpinner,
    FaExclamationTriangle,
    FaTimes,
    FaUserCircle,
    FaBuilding,
    FaCalendarAlt,
    FaEnvelope,
    FaPhone,
    FaBriefcase,
    FaMapMarkerAlt,
    FaGraduationCap,
    FaUserFriends,
    FaChild,
    FaClock,
    FaMedkit,
    FaPlane,
    FaBaby,
    FaMoneyBillWave,
    FaFile,
    FaDownload,
    FaSearchPlus,
    FaSearchMinus,
    FaStethoscope,
    FaPhoneAlt,
    FaFilter,
    FaFileExcel,
    FaFilePdf,
    FaPrint
} from 'react-icons/fa';
import Topbar from '../layouts/Topbar';
import Sidebar from '../layouts/Sidebar';
import './ManagerEmployees.css';
import packageJson from '../../../../package.json';
import { Modal, Box } from '@mui/material';
import Select from 'react-select';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

const ManagerEmployees = () => {
    const [modalContent, setModalContent] = useState(null);
    const navigate = useNavigate();
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [updateModalOpen, setUpdateModalOpen] = useState(false);
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({
        email: '',
        first_name: '',
        last_name: '',
        phone_number: '',
        department: '',
        salary: '',
        gender: '',
        home_address: '',
        position: '',
        
    });
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [departments, setDepartments] = useState([]);
    const [showPhotoModal, setShowPhotoModal] = useState(false);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [photoUrl, setPhotoUrl] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [departmentFilter, setDepartmentFilter] = useState('all');
    const [exportLoading, setExportLoading] = useState(false);

    const baseUrl = "https://api.burrayhr.com";

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const token = localStorage.getItem('token');
            
            // Fetch employees
            const employeesResponse = await axios.get(`${baseUrl}/api/employees/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            console.log('Employees:', employeesResponse.data);
            setEmployees(employeesResponse.data);
            
            // Fetch departments
            const departmentsResponse = await axios.get(`${baseUrl}/api/departments/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            const departmentOptions = departmentsResponse.data.map(dept => ({
                value: dept.id,
                label: dept.name
            }));
            
            setDepartments(departmentOptions);
            setError(null);
        } catch (error) {
            console.error('Error fetching data:', error);
            setError('Failed to fetch data. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleCategoryFilterChange = (e) => {
        setCategoryFilter(e.target.value);
    };

    const handleDepartmentFilterChange = (e) => {
        setDepartmentFilter(e.target.value);
    };

    const filteredEmployees = employees.filter(employee => {
        const matchesSearch = 
            employee.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            employee.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (employee.department_name || '').toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesCategory = 
            categoryFilter === 'all' || 
            (employee.category && employee.category.toLowerCase() === categoryFilter.toLowerCase());
        
        const matchesDepartment = 
            departmentFilter === 'all' || 
            (employee.department_name && employee.department_name.toString() === departmentFilter);
        
        return matchesSearch && matchesCategory && matchesDepartment;
    });

    const handleEdit = (employee) => {
        setSelectedEmployee(employee);
        setFormData({
            email: employee.email,
            first_name: employee.first_name,
            last_name: employee.last_name,
            phone_number: employee.phone_number,
            department: employee.department?.id,
            salary: employee.salary,
            gender: employee.gender,
            home_address: employee.home_address || '',
            highest_qualification: employee.highest_qualification || '',
            position: employee.position || '',
            documents: employee.documents || null
        });
        setSelectedDepartment({
            value: employee.department?.id,
            label: employee.department?.name
        });
        setUpdateModalOpen(true);
    };

    const handleDelete = async (employee) => {
        try {
            const result = await Swal.fire({
                title: 'Are you sure?',
                text: `Do you want to delete ${employee.first_name} ${employee.last_name}?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#1e3a8a',
                cancelButtonColor: '#dc2626',
                confirmButtonText: 'Yes, delete',
                cancelButtonText: 'Cancel'
            });

            if (result.isConfirmed) {
                const token = localStorage.getItem('token');
                await axios.delete(`${baseUrl}/api/employees/${employee.id}/`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                Swal.fire({
                    icon: 'success',
                    title: 'Deleted!',
                    text: 'Employee has been deleted successfully.',
                    confirmButtonColor: '#1e3a8a'
                });

                fetchData();
            }
        } catch (error) {
            console.error('Error deleting employee:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to delete employee. Please try again.',
                confirmButtonColor: '#1e3a8a'
            });
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        const formattedDate = date.toLocaleDateString('en-US'); // Example: 03/02/2025
        
        // if (isNaN(formattedDate)) return 'Invalid Date';
        
        // Get day, month, and year components
        const day = date.getDate().toString().padStart(2, '0');
        const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
        const month = monthNames[date.getMonth()]; // Month is 0-indexed
        const year = date.getFullYear();
        
        // Return in dd Month yyyy format
        return `${day} ${month} ${year}`;
    };

    const handleRowClick = (employee) => {
        setSelectedEmployee(employee);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedEmployee(null);
    };

    const calculateTimeRemaining = (contract) => {
        if (!contract) {
            return null;
        }
        
        // Check if end_date exists either in contract or directly in the employee object
        const endDate = contract.end_date || contract.contract_end_date;
        
        if (contract.type !== 'fixed_term' || !endDate) {
            return null;
        }

        const today = new Date();
        const contractEnd = new Date(endDate);
        const timeDiff = contractEnd - today;
        
        // If contract has expired
        if (timeDiff < 1) {
            return 'Contract Expired';
        }

        // Calculate total days remaining
        const totalDays = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
        
        // Calculate years
        const years = Math.floor(totalDays / 365);
        
        // Calculate remaining months after years
        const remainingDaysAfterYears = totalDays % 365;
        const months = Math.floor(remainingDaysAfterYears / 30);
        
        // Calculate remaining days after months
        const days = remainingDaysAfterYears % 30;

        // Build the time remaining string
        let timeString = '';
        
        if (years > 0) {
            timeString += `${years} year${years > 1 ? 's' : ''}, `;
        }
        
        if (months > 0 || years > 0) {
            timeString += `${months} month${months > 1 ? 's' : ''}, `;
        }
        
        timeString += `${days} day${days > 1 ? 's' : ''} remaining`;

        return timeString;
    };

    const PhotoModal = ({ photoUrl, onClose }) => {
        const handleZoomIn = (e) => {
            e.stopPropagation();
            setZoomLevel(prev => Math.min(prev + 0.2, 3));
        };

        const handleZoomOut = (e) => {
            e.stopPropagation();
            setZoomLevel(prev => Math.max(prev - 0.2, 0.5));
        };

        return (
            <div className="photo-modal-backdrop" onClick={onClose}>
                <div className="photo-modal-content" onClick={e => e.stopPropagation()}>
                    <div className="photo-modal-controls">
                        <button onClick={handleZoomIn} className="zoom-button">
                            <FaSearchPlus />
                        </button>
                        <button onClick={handleZoomOut} className="zoom-button">
                            <FaSearchMinus />
                        </button>
                        <button onClick={onClose} className="close-photo-btn">
                            <FaTimes />
                        </button>
                    </div>
                    <div className="photo-modal-image-container">
                        <img 
                            src={photoUrl} 
                            alt="Employee" 
                            style={{ transform: `scale(${zoomLevel})` }}
                            className="photo-modal-image"
                        />
                    </div>
                </div>
            </div>
        );
    };

    const EmployeeModal = ({ employee, onClose }) => {
        if (!employee) return null;

        const handlePhotoClick = () => {
            if (employee.photo_url) {
                setPhotoUrl(employee.photo_url);
                setZoomLevel(1);
                setShowPhotoModal(true);
            }
        };

        // Check if contract details are in contract_details or directly in employee object
        const contractType = employee.contract_details?.type || employee.contract_type;
        const contractEndDate = employee.contract_details?.end_date || employee.contract_end_date || employee.end_date;
        
        // Pass the appropriate contract object to calculateTimeRemaining
        const contractForTimeRemaining = {
            type: contractType,
            end_date: contractEndDate
        };
        
        const timeRemaining = calculateTimeRemaining(contractForTimeRemaining);

        const handlePrintEmployee = () => {
            // Create a new window for printing
            const printWindow = window.open('', '_blank');
            
            // Generate HTML content for printing
            const printContent = `
                <!DOCTYPE html>
                <html>
                <head>
                    <title>Employee Profile - ${employee.first_name} ${employee.last_name}</title>
                    <style>
                        body {
                            font-family: Arial, sans-serif;
                            line-height: 1.6;
                            color: #333;
                            max-width: 800px;
                            margin: 0 auto;
                            padding: 20px;
                        }
                        .header {
                            text-align: center;
                            margin-bottom: 30px;
                            border-bottom: 2px solid #1e3a8a;
                            padding-bottom: 10px;
                        }
                        .header h1 {
                            color: #1e3a8a;
                            margin-bottom: 5px;
                        }
                        .header p {
                            color: #64748b;
                            margin: 0;
                        }
                        .section {
                            margin-bottom: 25px;
                        }
                        .section h2 {
                            color: #1e3a8a;
                            border-bottom: 1px solid #e5e7eb;
                            padding-bottom: 5px;
                            margin-bottom: 15px;
                        }
                        .info-grid {
                            display: grid;
                            grid-template-columns: repeat(2, 1fr);
                            gap: 15px;
                        }
                        .info-item {
                            margin-bottom: 10px;
                        }
                        .info-item label {
                            display: block;
                            font-weight: bold;
                            color: #64748b;
                            font-size: 0.9em;
                            margin-bottom: 3px;
                        }
                        .info-item p {
                            margin: 0;
                        }
                        .footer {
                            text-align: center;
                            margin-top: 30px;
                            font-size: 0.8em;
                            color: #64748b;
                            border-top: 1px solid #e5e7eb;
                            padding-top: 10px;
                        }
                        @media print {
                            body {
                                padding: 0;
                            }
                            .no-print {
                                display: none;
                            }
                        }
                    </style>
                </head>
                <body>
                    <div class="header" style={{ 
                        marginTop: window.innerWidth <= 768 ? '20px' : '0',
                        '@media (max-width: 768px)': {
                            marginTop: '20px'
                        }
                    }}>
                        <h1>${employee.first_name} ${employee.last_name}</h1>
                        <p>${employee.position || 'Position not set'}</p>
                        <p>Employee ID: ${employee.employee_id}</p>
                    </div>
                    
                    <div class="section">
                        <h2>Personal Information</h2>
                        <div class="info-grid">
                            <div class="info-item">
                                <label>Date of Birth</label>
                                <p>${formatDate(employee.date_of_birth) || 'Not provided'}</p>
                            </div>
                            <div class="info-item">
                                <label>Gender</label>
                                <p>${employee.gender || 'Not provided'}</p>
                            </div>
                            <div class="info-item">
                                <label>Department</label>
                                <p>${employee.department_name || 'Not assigned'}</p>
                            </div>
                            <div class="info-item">
                                <label>Category</label>
                                <p>${employee.category || 'Not specified'}</p>
                            </div>
                            <div class="info-item">
                                <label>Working Days/Month</label>
                                <p>${employee.working_days_per_month || '22'} days</p>
                            </div>
                        </div>
                    </div>
                    
                    <div class="section">
                        <h2>Contact Information</h2>
                        <div class="info-grid">
                            
                            <div class="info-item">
                                <label>Phone</label>
                                <p>${employee.phone_number}</p>
                            </div>
                            <div class="info-item">
                                <label>Home Address</label>
                                <p>${employee.home_address || 'Not provided'}</p>
                            </div>
                            
                        </div>
                    </div>
                    
                    <div class="section">
                        <h2>Contract Details</h2>
                        <div class="info-grid">
                            <div class="info-item">
                                <label>Contract Type</label>
                                <p>${contractType ? contractType.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase()) : 'N/A'} 
                                    {(() => {
                                        const today = new Date();
                                        const probationEndDate = employee.contract_details?.probation_end_date ? new Date(employee.contract_details.probation_end_date) : null;
                                        let status = ' (Permanent)';
                                        
                                        if (probationEndDate && today <= probationEndDate) {
                                            status = ' (Probation Period)';
                                        } else if (employee.contract_details?.p === 'probation') {
                                            status = ' (Probation)';
                                        }
                                        
                                        return status;
                                    })()}</p>
                            </div>
                            <div class="info-item">
                                <label>Start Date</label>
                                <p>${formatDate(employee.contract_details?.start_date || employee.contract_start_date)}</p>
                            </div>
                            ${contractType === 'fixed_term' ? `
                            <div class="info-item">
                                <label>End Date</label>
                                <p>${formatDate(contractEndDate)}</p>
                            </div>
                            ${timeRemaining ? `
                            <div class="info-item">
                                <label>Contract Expiration</label>
                                <p>${timeRemaining}</p>
                            </div>
                            ` : ''}
                            ` : ''}
                            <div class="info-item">
                                <label>Years of Service</label>
                                <p>${employee.length_of_service} years</p>
                            </div>
                        </div>
                    </div>
                    
                    <div class="section">
                        <h2>Salary Information</h2>
                        <div class="info-grid">
                            <div class="info-item">
                                <label>Basic Salary</label>
                                <p>$${(employee.contract_details?.salary || 'N/A').toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</p>
                            </div>
                            ${employee.allowances && employee.allowances.length > 0 ? `
                            <div class="info-item">
                                <label>Allowances</label>
                                <p>${employee.allowances.map(allowance => 
                                    `${allowance.allowance_type === 'other' 
                                        ? (allowance.title || 'Other') 
                                        : allowance.allowance_type.charAt(0).toUpperCase() + allowance.allowance_type.slice(1)
                                    }: $${Number(allowance.amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
                                ).join('<br>')}</p>
                            </div>
                            ` : ''}
                            <div class="info-item">
                                <label>Total Income</label>
                                <p>$${(employee.total_income || 'N/A').toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</p>
                            </div>
                        </div>
                    </div>
                    
                    <div class="section">
                        <h2>Next of Kin Details</h2>
                        <div class="info-grid">
                            <div class="info-item">
                                <label>Name</label>
                                <p>${employee.next_of_kin_name || 'Not provided'}</p>
                            </div>
                            <div class="info-item">
                                <label>Contact</label>
                                <p>${employee.next_of_kin_contact || 'Not provided'}</p>
                            </div>
                            <div class="info-item">
                                <label>Address</label>
                                <p>${employee.next_of_kin_address || 'Not provided'}</p>
                            </div>
                            <div class="info-item">
                                <label>Relationship</label>
                                <p>${employee.next_of_kin_relationship || 'Not provided'}</p>
                            </div>
                        </div>
                    </div>
                    
                    <div class="footer">
                        <p>Generated on ${new Date().toLocaleDateString()} | Burray HR Management System</p>
                    </div>
                    
                    <div class="no-print" style="text-align: center; margin-top: 20px;">
                        <button onclick="window.print()" style="padding: 10px 20px; background-color: #1e3a8a; color: white; border: none; border-radius: 4px; cursor: pointer;">Print</button>
                    </div>
                </body>
                </html>
            `;
            
            // Write the content to the new window
            printWindow.document.write(printContent);
            printWindow.document.close();
            
            // Wait for content to load before printing
            printWindow.onload = function() {
                printWindow.focus();
                // Automatically trigger print dialog
                printWindow.print();
            };
        };

        const handleDownloadPDF = () => {
            // Create a new PDF document
            const doc = new jsPDF();
            
            // Add title
            doc.setFontSize(16);
            doc.text(`${employee.first_name} ${employee.last_name}`, 14, 15);
            
            // Add subtitle
            doc.setFontSize(12);
            doc.text(`Employee ID: ${employee.employee_id}`, 14, 25);
            doc.text(`Position: ${employee.position || 'Position not set'}`, 14, 32);
            
            // Add date
            doc.setFontSize(10);
            doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 40);
            
            // Personal Information
            doc.setFontSize(12);
            doc.text('Personal Information', 14, 50);
            
            // Prepare data for table
            const personalData = [
                ['Date of Birth', formatDate(employee.date_of_birth) || 'Not provided'],
                ['Gender', employee.gender || 'Not provided'],
                ['Department', employee.department_name || 'Not assigned'],
                ['Category', employee.category || 'Not specified'],
                ['Working Days/Month', `${employee.working_days_per_month || '22'} days`]
            ];
            
            // Add table
            doc.autoTable({
                startY: 55,
                head: [['Field', 'Value']],
                body: personalData,
                theme: 'grid',
                headStyles: {
                    fillColor: [30, 58, 138],
                    textColor: 255,
                    fontSize: 10,
                    fontStyle: 'bold'
                },
                bodyStyles: {
                    fontSize: 9
                },
                alternateRowStyles: {
                    fillColor: [245, 245, 245]
                }
            });
            
            // Contact Information
            const contactY = doc.lastAutoTable.finalY + 10;
            doc.setFontSize(12);
            doc.text('Contact Information', 14, contactY);
            
            const contactData = [
                ['Email', employee.email],
                ['Phone', employee.phone_number],
                ['Home Address', employee.home_address || 'Not provided'],
                ['Location', employee.location || 'Not provided']
            ];
            
            doc.autoTable({
                startY: contactY + 5,
                head: [['Field', 'Value']],
                body: contactData,
                theme: 'grid',
                headStyles: {
                    fillColor: [30, 58, 138],
                    textColor: 255,
                    fontSize: 10,
                    fontStyle: 'bold'
                },
                bodyStyles: {
                    fontSize: 9
                },
                alternateRowStyles: {
                    fillColor: [245, 245, 245]
                }
            });
            
            // Contract Details
            const contractY = doc.lastAutoTable.finalY + 10;
            doc.setFontSize(12);
            doc.text('Contract Details', 14, contractY);
            
            const contractData = [
                ['Contract Type', contractType ? contractType.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase()) : 'N/A'],
                ['Start Date', formatDate(employee.contract_details?.start_date || employee.contract_start_date)],
                ['Years of Service', `${employee.length_of_service} years`]
            ];
            
            if (contractType === 'fixed_term') {
                contractData.push(['End Date', formatDate(contractEndDate)]);
                if (timeRemaining) {
                    contractData.push(['Contract Expiration', timeRemaining]);
                }
            }
            
            doc.autoTable({
                startY: contractY + 5,
                head: [['Field', 'Value']],
                body: contractData,
                theme: 'grid',
                headStyles: {
                    fillColor: [30, 58, 138],
                    textColor: 255,
                    fontSize: 10,
                    fontStyle: 'bold'
                },
                bodyStyles: {
                    fontSize: 9
                },
                alternateRowStyles: {
                    fillColor: [245, 245, 245]
                }
            });
            
            // Salary Information
            const salaryY = doc.lastAutoTable.finalY + 10;
            doc.setFontSize(12);
            doc.text('Salary Information', 14, salaryY);
            
            const salaryData = [
                ['Basic Salary', `$${(employee.contract_details?.salary || 'N/A').toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`],
                ['Total Income', `$${(employee.total_income || 'N/A').toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`]
            ];
            
            if (employee.allowances && employee.allowances.length > 0) {
                employee.allowances.forEach(allowance => {
                    const allowanceType = allowance.allowance_type === 'other' 
                        ? (allowance.title || 'Other') 
                        : allowance.allowance_type.charAt(0).toUpperCase() + allowance.allowance_type.slice(1);
                    
                    salaryData.push([
                        allowanceType,
                        `$${Number(allowance.amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
                    ]);
                });
            }
            
            doc.autoTable({
                startY: salaryY + 5,
                head: [['Field', 'Value']],
                body: salaryData,
                theme: 'grid',
                headStyles: {
                    fillColor: [30, 58, 138],
                    textColor: 255,
                    fontSize: 10,
                    fontStyle: 'bold'
                },
                bodyStyles: {
                    fontSize: 9
                },
                alternateRowStyles: {
                    fillColor: [245, 245, 245]
                }
            });
            
            // Next of Kin
            const kinY = doc.lastAutoTable.finalY + 10;
            doc.setFontSize(12);
            doc.text('Next of Kin Details', 14, kinY);
            
            const kinData = [
                ['Name', employee.next_of_kin_name || 'Not provided'],
                ['Contact', employee.next_of_kin_contact || 'Not provided'],
                ['Address', employee.next_of_kin_address || 'Not provided'],
                ['Relationship', employee.next_of_kin_relationship || 'Not provided']
            ];
            
            doc.autoTable({
                startY: kinY + 5,
                head: [['Field', 'Value']],
                body: kinData,
                theme: 'grid',
                headStyles: {
                    fillColor: [30, 58, 138],
                    textColor: 255,
                    fontSize: 10,
                    fontStyle: 'bold'
                },
                bodyStyles: {
                    fontSize: 9
                },
                alternateRowStyles: {
                    fillColor: [245, 245, 245]
                }
            });
            
            // Save PDF
            doc.save(`${employee.first_name}_${employee.last_name}_profile.pdf`);
        };

        return (
            <div className="admin-modal-backdrop" onClick={onClose}>
                <div className="admin-employee-modal" onClick={e => e.stopPropagation()}>
                    <div className="admin-modal-header">
                        <div className="admin-modal-title">
                            <div 
                                className="admin-employee-photo-container" 
                                onClick={handlePhotoClick}
                                style={{ cursor: employee.photo_url ? 'pointer' : 'default' }}
                            >
                                {employee.photo_url ? (
                                    <img 
                                        style={{width: '100px', height: '100px'}}
                                        src={employee.photo_url} 
                                        alt={`${employee.first_name} ${employee.last_name}`}
                                        className="passport-photo"
                                    />
                                ) : (
                                    <FaUserCircle className="admin-default-photo" />
                                )}
                                {employee.photo_url && (
                                    <div className="admin-photo-overlay">
                                        <FaSearchPlus className="admin-zoom-icon" />
                                    </div>
                                )}
                            </div>
                            <div style={{ 
                                marginTop: window.innerWidth <= 768 ? '30px' : '0',
                                '@media (max-width: 768px)': {
                                    marginTop: '20px'
                                }
                            }}>
                                <h2 style={{color: '#fff'}}>{employee.first_name} {employee.last_name}</h2>
                                <p className="admin-employee-position mt-2"><b>Job Title:</b> &nbsp; &nbsp; {employee.position || 'Position not set'}</p>
                                <span className="admin-employee-id"><strong>Employee ID:</strong> &nbsp; &nbsp; {employee.employee_id}</span>
                            </div>
                        </div>
                        <div className="admin-modal-actions">
                            <button 
                                className="admin-action-btn print-btn" 
                                onClick={handlePrintEmployee}
                                title="Print Employee Data"
                            >
                                <FaPrint />
                            </button>
                            <button className="admin-close-modal-btn" onClick={onClose}>
                                <FaTimes />
                            </button>
                        </div>
                    </div>

                    <div className="admin-modal-content">
                        <div className="admin-info-section">
                            <h3>Personal Information</h3>
                            <div className="admin-info-grid">
                                <div className="admin-info-item">
                                    <FaCalendarAlt className="admin-info-icon" />
                                    <div style={{display: 'inline-block', flexDirection: 'column', gap: '0.5rem'}}>
                                        <span style={{fontWeight: '400'}}>Date of Birth:</span>
                                        &nbsp;
                                        &nbsp;
                                        <span style={{fontWeight: '500', textAlign: 'right'}}>{formatDate(employee.date_of_birth) || 'Not provided'}</span>
                                    </div>
                                </div>
                                <div className="admin-info-item">
                                    <FaUserFriends className="admin-info-icon" />
                                    <div style={{display: 'inline-block', flexDirection: 'column', gap: '0.5rem'}}>
                                        <span style={{fontWeight: '400'}}>Gender:</span>
                                        &nbsp;
                                        &nbsp;
                                        <span style={{fontWeight: '500', textAlign: 'right', marginRight: '10px'}} className="admin-capitalize">{employee.gender || 'Not provided'}</span>
                                    </div>
                                </div>
                                <div className="admin-info-item">
                                    <FaBuilding className="admin-info-icon" />
                                    <div style={{display: 'inline-block', flexDirection: 'column', gap: '0.5rem'}}>
                                        <span style={{fontWeight: '400'}}>Department:</span>
                                        &nbsp;
                                        &nbsp;
                                        <span style={{fontWeight: '500'}}>{employee.department_name || 'Not assigned'}</span>
                                    </div>
                                </div>
                                <div className="admin-info-item">
                                    <FaBriefcase className="admin-info-icon" />
                                    <div style={{display: 'inline-block', flexDirection: 'column', gap: '0.5rem'}}>
                                        <span style={{fontWeight: '400'}}>Category:</span>
                                        &nbsp;
                                        &nbsp;
                                        <span style={{fontWeight: '500'}} className="capitalize">{employee.category || 'Not specified'}</span>
                                    </div>
                                </div>
                                <div className="admin-info-item">
                                    <FaClock className="admin-info-icon" />
                                    <div>
                                        <span style={{fontWeight: '400'}}>Working Days/Month:</span>
                                        &nbsp;
                                        &nbsp;
                                        <span style={{fontWeight: '500'}}>{employee.working_days_per_month || '22'} days</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="info-section">
                            <h4>Contact Information</h4>
                            <div className="admin-info-grid">
                                
                                <div className="admin-info-item">
                                    <FaPhone className="admin-info-icon" />
                                    <div>
                                        <span style={{fontWeight: '400'}}>Phone:</span>
                                        &nbsp;
                                        &nbsp;
                                        <span style={{fontWeight: '500'}}>{employee.phone_number}</span>
                                    </div>
                                </div>
                                <div className="admin-info-item">
                                    <FaMapMarkerAlt className="admin-info-icon" />
                                    <div>
                                        <span style={{fontWeight: '400'}}>Home Address:</span>
                                        &nbsp;
                                        &nbsp;
                                        <span style={{fontWeight: '500'}}>{employee.home_address || 'Not provided'}</span>
                                    </div>
                                </div>
                                
                            </div>
                        </div>

                        <div className="info-section">
                            <h4>Contract Details</h4>
                            <div className="admin-info-grid">
                                <div className="admin-info-item">
                                    <FaCalendarAlt className="admin-info-icon" />
                                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                        <span style={{ fontWeight: '600' }}>Contract Type:</span>
                                        <span style={{ fontWeight: '500' }} className="capitalize">
                                            {contractType ? contractType.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase()) : 'N/A'} 
                                            {(() => {

                                                const today = new Date();
                                                const probationEndDate = employee.contract_details?.probation_end_date ? new Date(employee.contract_details.probation_end_date) : null;
                                                let status = '';
                                                
                                                if (probationEndDate && today <= probationEndDate) {
                                                    status = ' (Probation Period)';
                                                } else if (employee.contract_details?.p === 'probation') {
                                                    status = ' (Probation)';
                                                }
                                                
                                                return status;
                                            })()}
                                        </span>
                                    </div>
                                </div>
                                <div className="admin-info-item">
                                    <FaCalendarAlt className="admin-info-icon" />
                                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                        <span style={{ fontWeight: '600' }}>Start Date:</span>
                                        <span style={{ fontWeight: '500' }} className="capitalize">{formatDate(employee.contract_details?.start_date || employee.contract_start_date)}</span>
                                    </div>
                                </div>
                                {(contractType === 'fixed_term') && (
                                    <>
                                        <div className="admin-info-item">
                                            <FaCalendarAlt className="admin-info-icon" />
                                            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                                <span style={{ fontWeight: '600' }}>End Date:</span>
                                                &nbsp;
                                                &nbsp;
                                                &nbsp;
                                                <span style={{ fontWeight: '500' }} className="capitalize">{formatDate(contractEndDate)}</span>
                                            </div>
                                        </div>
                                        {timeRemaining && (
                                            <div className="admin-info-item">
                                                <FaClock className="admin-info-icon" />
                                                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                                    <span style={{ fontWeight: '600' }}>Contract Expiration:</span>
                                                    &nbsp;
                                                    &nbsp;
                                                    &nbsp;
                                                    <span style={{ fontWeight: '500', position: 'relative', right: '0' }}  className={`time-remaining ${timeRemaining < 1 || timeRemaining === 'Contract Expired' ? 'expired' : ''}`}>
                                                        {timeRemaining === '0' ? 'Contract Expired' : timeRemaining}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                                <div className="admin-info-item">
                                    <FaCalendarAlt className="admin-info-icon" />
                                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                        <span style={{ fontWeight: '600' }}>Years of Service:</span>
                                        &nbsp;
                                        &nbsp;
                                        &nbsp;
                                        <span style={{ fontWeight: '500' }} className="capitalize">{employee.length_of_service} years</span>
                                    </div>
                                </div>
                                <div className="info-item">
                                    <FaMoneyBillWave className="admin-info-icon" />
                                    <div style={{ display: 'inline-block', justifyContent: 'space-between', width: '100%', alignItems: 'center' }} className="">
                                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                            <span className="admin-salary-label">Basic Salary:</span>
                                            <span style={{ color: 'blue', fontWeight: '700' }} className="capitalize">
                                                ${Number(employee.contract_details?.salary || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                            </span>
                                        </div>
                                        
                                        {employee.allowances && employee.allowances.length > 0 && (
                                            <div className="admin-salary-item">
                                                <span style={{ textDecoration: 'underline', fontSize: '1rem', fontWeight: 'bold', marginLeft: '0' }} className="admin-salary-label mt-3">Allowances</span>
                                                <ul className="admin-allowances-list">
                                                    {employee.allowances.map((allowance, index) => (
                                                        <li key={index} className="admin-allowance-item mt-2">
                                                            <span className="admin-allowance-type">
                                                                {allowance.allowance_type === 'other' 
                                                                    ? (allowance.title || 'Other')
                                                                    : allowance.allowance_type.charAt(0).toUpperCase() + allowance.allowance_type.slice(1)
                                                                }
                                                            </span>
                                                            <span style={{ color: '#2563eb', fontWeight: '700', marginLeft: '0.5rem' }} className="allowance-amount capitalize">
                                                                ${Number(allowance.amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                            </span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        <hr />
                                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }} className="mt-3">
                                            <span className="admin-salary-label">Total Income: </span>
                                            &nbsp;
                                            &nbsp;
                                            &nbsp;
                                            <span style={{ color: '#2563eb', fontWeight: '700' }} className="capitalize">
                                                ${Number(employee.total_income || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="info-section">
                            <h4><FaMoneyBillWave className="section-icon" /> Liabilities</h4>
                            {contractType !== 'student' && (
                                <div className="admin-info-grid">
                                    
                                    {employee.contract_details?.contract_type === 'fixed_term' && (
                                        <div className="admin-info-item">
                                            <FaMoneyBillWave className="admin-info-icon" />
                                            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                                <span style={{ fontWeight: '600' }}>Notice Pay:</span>
                                                &nbsp;
                                                &nbsp;
                                                &nbsp;
                                                <span style={{ fontWeight: '500', color: '#2563eb', fontWeight: '500' }} className="capitalize">
                                                    ${Number(employee.contract_details?.notice_pay || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                    {employee.contract_details?.type === 'open' && (
                                        <>
                                            <div className="admin-info-item">
                                                <FaMoneyBillWave className="admin-info-icon" />
                                                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                                    <span style={{ fontWeight: '600' }}>Notice Pay:</span>
                                                    &nbsp;
                                                    &nbsp;
                                                    &nbsp;
                                                    <span style={{ fontWeight: '500', color: '#2563eb', fontWeight: '500' }} className="capitalize">
                                                        ${Number(employee.contract_details?.notice_pay || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="admin-info-item">
                                                <FaMoneyBillWave className="admin-info-icon" />
                                                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                                    <span style={{ fontWeight: '600' }}>Service Pay:</span>
                                                    &nbsp;
                                                    &nbsp;
                                                    &nbsp;
                                                    <span style={{ fontWeight: '500', color: '#2563eb', fontWeight: '500' }} className="capitalize">
                                                        ${Number(employee.contract_details?.service_pay || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                    </span>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                    {employee.category === 'worker' && (
                                        <div className="admin-info-item">
                                            <FaMoneyBillWave className="admin-info-icon" />
                                            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                                <span style={{ fontWeight: '600' }}>Gratuity:</span>
                                                &nbsp;
                                                &nbsp;
                                                &nbsp;
                                                <span style={{ fontWeight: '500', color: '#2563eb', fontWeight: '500' }} className="capitalize">
                                                    ${Number(employee.calculate_gratuity_amount || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                    <div className="admin-info-item">
                                        <FaMoneyBillWave className="admin-info-icon" />
                                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                            <span style={{ fontWeight: '600' }}>Total Liabilities:</span>
                                            &nbsp;
                                            &nbsp;
                                            &nbsp;
                                            <span className="capitalize" style={{ color: '#2563eb', fontWeight: '500' }}>
                                                ${(() => {
                                                    const noticePay = employee.contract_details?.type === 'open' ? 
                                                        Number(employee.contract_details?.notice_pay || 0) : 0;
                                                    const servicePay = employee.contract_details?.type === 'open' ? 
                                                        Number(employee.contract_details?.service_pay || 0) : 0;
                                                    const gratuity = employee.category === 'worker' ? 
                                                        Number(employee.calculate_gratuity_amount || 0) : 0;
                                                    const total = noticePay + servicePay + gratuity;
                                                    return total.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                                                })()}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="info-section">
                            <h4>Next of Kin Details</h4>
                            <div className="admin-info-grid">
                                <div className="admin-info-item">
                                    <FaUserFriends className="admin-info-icon" />
                                    <div style={{ display: 'inline-block', alignItems: 'center', gap: '0.5rem' }}>
                                        <span style={{ fontWeight: '600' }}>Name:</span>
                                        &nbsp;
                                        &nbsp;
                                        &nbsp;
                                        <span style={{ fontWeight: '500' }}>{employee.next_of_kin_name || 'Not provided'}</span>
                                    </div>
                                </div>
                                <div className="admin-info-item">
                                    <FaPhone className="admin-info-icon" />
                                    <div>
                                        <span style={{ fontWeight: '600' }}>Contact:</span>
                                        &nbsp;
                                        &nbsp;
                                        &nbsp;
                                        <span style={{ fontWeight: '500' }}>{employee.next_of_kin_contact || 'Not provided'}</span>
                                    </div>
                                </div>
                                <div className="admin-info-item">
                                    <FaMapMarkerAlt className="admin-info-icon" />
                                    <div style={{ display: 'inline-block', alignItems: 'center', gap: '0.5rem' }}>
                                        <span style={{ fontWeight: '600' }}>Address:</span>
                                        &nbsp;
                                        &nbsp;
                                        &nbsp;
                                        <span style={{ fontWeight: '500' }}>{employee.next_of_kin_address || 'Not provided'}</span>
                                    </div>
                                </div>
                                <div className="admin-info-item">
                                    <FaUserFriends className="admin-info-icon" />
                                    <div style={{ display: 'inline-block', alignItems: 'center', gap: '0.5rem' }}>
                                        <span style={{ fontWeight: '600' }}>Relationship:</span>
                                        &nbsp;
                                        &nbsp;
                                        &nbsp;
                                        <span style={{ fontWeight: '500' }}>{employee.next_of_kin_relationship || 'Not provided'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Add Documents Section */}
                        {employee.documents && employee.documents.document_file && (
                            <div className="admin-info-section">
                                <h3>Documents</h3>
                                <div className="admin-documents-grid">
                                    <div className="admin-document-item">
                                        <FaFile className="admin-document-icon" />
                                        <div className="admin-document-info">
                                            <h4>Employee Document</h4>
                                            <p>{typeof employee.documents.document_file === 'string' 
                                                ? employee.documents.document_file.split('/').pop() 
                                                : employee.documents.document_file.name}</p>
                                            <a 
                                                href={employee.documents.document_file} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="document-link"
                                            >
                                                <FaDownload /> View Document
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {employee.children && employee.children.length > 0 && (
                            <div className="admin-info-section">
                                <h3>Children</h3>
                                <div className="admin-children-grid">
                                    {employee.children.map(child => (
                                        <div key={child.id} className="admin-child-card">
                                            <FaChild className="admin-child-icon" />
                                            <div className="admin-child-info">
                                                <h4>{child.name}</h4>
                                                <p>{child.age} years old • {child.gender}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {employee.qualifications && employee.qualifications.length > 0 && (
                            <div className="admin-info-section">
                                <h3>Qualifications</h3>
                                <div className="admin-documents-grid">
                                    {employee.qualifications.map(qualification => (
                                        <div 
                                            key={qualification.id} 
                                            className="admin-document-item"
                                            style={{ cursor: 'pointer' }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                        >
                                            <div className="admin-document-info">
                                                <h6>{qualification.title}</h6>
                                                
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {employee.documents && employee.documents.length > 0 && (
                            <div className="admin-info-section">
                                <h3>Personal Documents</h3>
                                <div className="admin-documents-grid">
                                    {employee.documents.map(document => (
                                        <div 
                                            key={document.id} 
                                            className="admin-document-item"
                                            onClick={() => window.open(document.document_url, '_blank')}
                                            style={{ cursor: 'pointer' }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                        >
                                            <FaFile className="admin-document-icon" />
                                            <div className="admin-document-info">
                                                <h4>{document.document_type.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())}</h4>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {employee.disciplinary_records && employee.disciplinary_records.length > 0 && (
                            <div className="admin-info-section">
                                <h3>Disciplinary Records</h3>
                                <div className="admin-documents-grid">
                                    {employee.disciplinary_records.map(record => (
                                        <div 
                                            key={record.id} 
                                            className="admin-document-item"
                                            onClick={() => window.open(record.record_file, '_blank')}
                                            style={{ cursor: 'pointer' }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                        >
                                            <FaFile className="admin-document-icon" />
                                            <div className="admin-document-info">
                                                <h4>{record.record_type.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())}</h4>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Helper functions for date formatting
    const formatDateForInput = (dateString) => {
        if (!dateString) return null;
        return dayjs(dateString);
    };

    const formatDateForDisplay = (dateString) => {
        if (!dateString) return '';
        const date = dayjs(dateString);
        return date.format('DD/MM/YYYY');
    };

    const handleDateChange = (date, fieldName) => {
        if (date) {
            // Format all dates to DD/MM/YYYY format
            const formattedDate = date.format('DD/MM/YYYY');
            
            setFormData(prev => ({
                ...prev,
                [fieldName]: formattedDate
            }));
            
            // Clear any errors for this field
            if (errors[fieldName]) {
                setErrors(prev => ({
                    ...prev,
                    [fieldName]: ''
                }));
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [fieldName]: ''
            }));
        }
    };

    const handleDepartmentChange = (selectedOption) => {
        setSelectedDepartment(selectedOption);
        setFormData(prev => ({
            ...prev,
            department: selectedOption?.value || ''
        }));
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        try {
            const token = localStorage.getItem('token');
            const formDataToSend = new FormData();
            
            // Add all text fields
            Object.keys(formData).forEach(key => {
                if (key === 'contract_start_date' || key === 'contract_end_date' || key === 'date_of_birth') {
                    // Convert all dates to DD/MM/YYYY format
                    if (formData[key]) {
                        const date = dayjs(formData[key]);
                        formDataToSend.append(key, date.format('DD/MM/YYYY'));
                    }
                } else {
                    formDataToSend.append(key, formData[key] || '');
                }
            });
            
            // Add document file if it exists and is a File object
            if (formData.document_file && formData.document_file instanceof File) {
                formDataToSend.append('document_file', formData.document_file);
            }

            await axios.put(
                `${baseUrl}/api/employees/${selectedEmployee.id}/`,
                formDataToSend,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            
            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Employee updated successfully',
                confirmButtonColor: '#1e3a8a'
            });
            
            setUpdateModalOpen(false);
            fetchData();
            
        } catch (error) {
            console.error('Error updating employee:', error);
            
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.message || 'Failed to update employee',
                confirmButtonColor: '#1e3a8a'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleExportExcel = async () => {
        try {
            setExportLoading(true);
            
            // Prepare data for export
            const exportData = filteredEmployees.map(employee => ({
                'Full Name': `${employee.first_name} ${employee.last_name}`,
                'Phone Number': employee.phone_number,
                'Gender': employee.gender,
                'Department': employee.department_name || 'No Department',
                'Position': employee.position,
                'Home Address': employee.home_address || 'Not provided',
                'Next of Kin': employee.next_of_kin_name || 'Not provided',
                'Next of Kin Contact': employee.next_of_kin_contact || 'Not provided',
                'Next of Kin Relationship': employee.next_of_kin_relationship || 'Not provided',
                'Next of Kin Address': employee.next_of_kin_address || 'Not provided',
                'Category': employee.category || 'Not specified',
                'Salary': employee.contract_details?.salary || employee.salary || 'Not provided',
                'Contract Type': employee.contract_details?.type || employee.contract_type || 'Not provided',
                'Start Date': formatDate(employee.contract_details?.start_date || employee.contract_start_date),
                'End Date': (employee.contract_details?.type || employee.contract_type) === 'fixed_term' 
                    ? formatDate(employee.contract_details?.end_date || employee.contract_end_date || employee.end_date)
                    : 'Permanent',
                
            }));

            // Create worksheet
            const ws = XLSX.utils.json_to_sheet(exportData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Employees');

            // Generate Excel file
            XLSX.writeFile(wb, `Employees_${new Date().toISOString().split('T')[0]}.xlsx`);

            Swal.fire({
                icon: 'success',
                title: 'Export Successful',
                text: 'Employee data has been exported to Excel',
                confirmButtonColor: '#1e3a8a',
                timer: 2000,
                timerProgressBar: true
            });
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            Swal.fire({
                icon: 'error',
                title: 'Export Failed',
                text: 'Failed to export employee data to Excel',
                confirmButtonColor: '#1e3a8a'
            });
        } finally {
            setExportLoading(false);
        }
    };

    const handleExportPDF = async () => {
        try {
            setExportLoading(true);
            
            // Create new PDF document
            const doc = new jsPDF();
            
            // Add title
            doc.setFontSize(16);
            doc.text('Employee List', 14, 15);
            
            // Add date
            doc.setFontSize(10);
            doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 25);
            
            // Define column widths
            const colWidths = [50, 40, 40, 30, 30, 30];
            const startX = 14;
            let currentY = 35;
            
            // Add headers
            doc.setFillColor(30, 58, 138);
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            
            const headers = ['Full Name', 'Department', 'Position', 'Category', 'Start Date', 'End Date'];
            let x = startX;
            headers.forEach((header, i) => {
                doc.rect(x, currentY, colWidths[i], 7, 'F');
                doc.text(header, x + 2, currentY + 5);
                x += colWidths[i];
            });
            
            // Add data rows
            doc.setTextColor(0, 0, 0);
            doc.setFont(undefined, 'normal');
            
            filteredEmployees.forEach((employee, rowIndex) => {
                currentY += 7;
                if (currentY > 270) {
                    doc.addPage();
                    currentY = 20;
                }
                
                // Alternate row colors
                if (rowIndex % 2 === 0) {
                    doc.setFillColor(245, 245, 245);
                } else {
                    doc.setFillColor(255, 255, 255);
                }
                
                const rowData = [
                    `${employee.first_name} ${employee.last_name}`,
                    employee.department_name || 'No Department',
                    employee.position,
                    employee.category || 'Not specified',
                    formatDate(employee.contract_details?.start_date || employee.contract_start_date),
                    (employee.contract_details?.type || employee.contract_type) === 'fixed_term' 
                        ? formatDate(employee.contract_details?.end_date || employee.contract_end_date || employee.end_date)
                        : 'Permanent'
                ];
                
                x = startX;
                rowData.forEach((text, i) => {
                    doc.rect(x, currentY, colWidths[i], 7, 'F');
                    doc.text(text.substring(0, 30), x + 2, currentY + 5);
                    x += colWidths[i];
                });
            });
            
            // Save PDF
            doc.save(`employees_${new Date().toISOString().split('T')[0]}.pdf`);

            Swal.fire({
                icon: 'success',
                title: 'Export Successful',
                text: 'Employee data has been exported to PDF',
                confirmButtonColor: '#1e3a8a',
                timer: 2000,
                timerProgressBar: true
            });
        } catch (error) {
            console.error('Error exporting to PDF:', error);
            Swal.fire({
                icon: 'error',
                title: 'Export Failed',
                text: 'Failed to export employee data to PDF',
                confirmButtonColor: '#1e3a8a'
            });
        } finally {
            setExportLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="loading-container">
                <Topbar/>
                <Sidebar/>
                <FaSpinner className="spinner" />
                <p>Loading employees...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="error-container">
                <Topbar/>
                <Sidebar/>
                <div className="error-content">
                    <FaExclamationTriangle className="error-icon" />
                    <p>{error}</p>
                    <button onClick={fetchData} className="retry-button">
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div>
            <Topbar />
            <Sidebar />
            <div className="main-content">
                <div className="employees-container">
                    <div className="employees-header">
                        <div className="title-section">
                            <h1>
                                <FaUsers className="title-icon" />
                                Employees
                            </h1>
                            <p className="subtitle">Manage your employees</p>
                        </div>
                        <div className="header-actions">
                            <div className="export-buttons">
                                <button 
                                    className="export-btn excel-btn"
                                    onClick={handleExportExcel}
                                    disabled={exportLoading}
                                    title="Export to Excel"
                                >
                                    <FaFileExcel /> Excel
                                </button>
                                
                            </div>
                            
                        </div>
                    </div>

                    <div className='row'>
                        <div className="col-md-4 filter-box pl-3">
                            <input
                                style={{width: '100%'}}
                                className="form-control"
                                type="text"
                                placeholder="Search employees..."
                                value={searchTerm}
                                onChange={handleSearch}
                            />
                        </div>

                        <div className="col-md-4 filter-box">
                            <FaFilter className="filter-icon" />
                            <select 
                                value={categoryFilter} 
                                onChange={handleCategoryFilterChange}
                                className="category-filter"
                            >
                                <option value="all">All Categories</option>
                                <option value="worker">Workers</option>
                                <option value="managerial">Managerial</option>
                            </select>
                        </div>
                    </div>

                    <div className="table-container">
                        <table className="employees-table">
                            <thead>
                                <tr>
                                    <th className="table-header">Full Name</th>
                                    <th className="table-header">Department</th>
                                    <th className="table-header">Position</th>
                                    <th className="table-header">Start Date</th>
                                    <th className="table-header">End Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredEmployees.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="no-results">
                                            No employees found
                                        </td>
                                    </tr>
                                ) : (
                                    filteredEmployees.map(employee => (
                                        <tr 
                                            key={employee.id} 
                                            onClick={() => handleRowClick(employee)}
                                            className="clickable-row"
                                        >
                                            <td className="table-cell">
                                                {employee.first_name} {employee.last_name}
                                            </td>
                                            <td className="table-cell">{employee.department_name || 'No Department'}</td>
                                            <td className="table-cell">{employee.position}</td>
                                            <td className="table-cell">{formatDate(employee.contract_details?.start_date || employee.contract_start_date)}</td>
                                            <td className="table-cell">
                                                {(employee.contract_details?.type || employee.contract_type) === 'fixed_term' 
                                                    ? formatDate(employee.contract_details?.end_date || employee.contract_end_date || employee.end_date)
                                                    : 'Permanent'}
                                            </td>
                                            
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {showModal && (
                    <EmployeeModal 
                        employee={selectedEmployee} 
                        onClose={closeModal} 
                    />
                )}
                {showPhotoModal && (
                    <PhotoModal 
                        photoUrl={photoUrl}
                        onClose={() => setShowPhotoModal(false)}
                    />
                )}
            </div>
        </div>
    );
};

export default ManagerEmployees;
